import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Car,
  Home as HomeIcon,
  Briefcase,
  Plane,
  Shield,
  Building,
  CheckCircle,
  Star,
  ChevronLeft,
  ChevronRight,
  Phone,
  Mail,
  MapPin,
  ArrowRight,
  Users,
  Award,
  Clock,
  ThumbsUp,
  PhoneCall,
} from "lucide-react";
import "./Home.css";
import PageSEO from '../components/PageSEO';


export default function Home() {
  const [reviewIndex, setReviewIndex] = useState(0);
  const navigate = useNavigate();

  // Navigate to /services and scroll to specific card
  function goToService(id) {
    navigate("/services");
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
  }

  // ── Real Google Reviews ──────────────────────────────────────────
  const reviews = [
    { name: "Jasraj J.",    rating: 5, text: "Great experience with Keystone Insurance. Their staff made the process of tweaking my coverage really easy and explained the coverage well. Inder did great at helping me to understand all my options. I'll definitely be coming back next year!" },
    { name: "Malwinder S.", rating: 5, text: "Keystone Insurance is a great place for you to get your insurance done. They are very professional and helpful. I had to go here multiple times for different tasks, and every time I went here, they attended me with a smile on their face. I never felt any discomfort whenever I went there and I never had to spend more than 10 minutes for me to get my work done. Thank you for such a great and flawless service." },
    { name: "CoreyRod S.",  rating: 5, text: "I like using this Insurance Auto plan location. The staff are very friendly, and knowledgeable about their business and rates. Some of the staff already know me by name when I go in, which is a pleasant bonus. I never have any issues finding parking there which is also nice. I couldn't be happier with an agent representing my automobile insurance needs. Great job!" },
    { name: "J C.",         rating: 5, text: "Just recently I went to this location… their service just blew me away. They tried so hard to find the perfect numbers for my new plates & they were nice enough to change my plates as well. Overall just magnificent service. I will be coming back to this location for all renewals. Thank you Keystone!" },
    { name: "Aye K.",       rating: 5, text: "I have been dealing with Devinder and company for many years now, very friendly and courteous service. They are always fast and efficient with good advice and suggestions. They are my only stop for all my insurance needs. Their customer service definitely sets them apart from the rest. I highly recommend!" },
    { name: "Omar T.",      rating: 5, text: "Very knowledgeable staff. They always make sure to help you out as much as needed. I have been using these guys for the past few years — they deserve a golden 5 stars." },
    { name: "Tyson S.",     rating: 5, text: "Great experience, staff is informed and educated on what they're doing making it an easy transaction, thanks." },
    { name: "Radhika B.",   rating: 5, text: "We personally recommend this insurance place. They are highly knowledgeable and adhere to the needs of the customers. Highly appreciate this place and their staff." },
    { name: "Din M.",       rating: 5, text: "I gave 5 stars because of good service and nice people. Very respectful. Best service all the time." },
    { name: "Amar G.",      rating: 5, text: "I highly recommend this place, incredibly friendly staff and highly knowledgeable. Took their time and answered all my questions and concerns." },
  ];

  const VISIBLE = 3;
  const total = reviews.length;
  const nextSlide = () => setReviewIndex((p) => (p + 1) % total);
  const prevSlide = () => setReviewIndex((p) => (p - 1 + total) % total);
  const displayed = Array.from({ length: VISIBLE }, (_, i) => reviews[(reviewIndex + i) % total]);

  const services = [
    { id: "auto-insurance",      icon: <Car />,       title: "Auto Insurance",      desc: "Personal and commercial vehicle coverage at competitive rates." },
    { id: "home-insurance",      icon: <HomeIcon />,  title: "Home Insurance",      desc: "Protect your home, condo, rental, or mobile home from the unexpected." },
    { id: "business-insurance",  icon: <Briefcase />, title: "Business Insurance",  desc: "Liability, property, and commercial coverage for businesses of all sizes." },
    { id: "travel-insurance",    icon: <Plane />,     title: "Travel Insurance",    desc: "Emergency medical, trip cancellation, and Visitor / Super Visa plans." },
    { id: "liability-insurance", icon: <Shield />,    title: "Liability Insurance", desc: "Personal and commercial liability protection when it matters most." },
    { id: "commercial-property", icon: <Building />,  title: "Commercial Property", desc: "From a small office to a large warehouse, your business premises and physical assets need proper protection." },
  ];

  const features = [
    { icon: <Award size={28} />,       title: "20+ Years of Experience", desc: "Two decades serving Surrey and the Greater Vancouver area." },
    { icon: <Users size={28} />,       title: "Independent Brokers",     desc: "We work for you — comparing dozens of insurers to find the best rate." },
    { icon: <Clock size={28} />,       title: "Fast Claims Support",     desc: "We guide you through every step of the claims process, start to finish." },
    { icon: <ThumbsUp size={28} />,    title: "No Obligation Quotes",    desc: "Free, pressure-free quotes tailored to your needs and budget." },
    { icon: <CheckCircle size={28} />, title: "Licensed Professionals",  desc: "All our advisors are fully licensed in British Columbia." },
    { icon: <Phone size={28} />,       title: "Local & Accessible",      desc: "Walk in, call, or email — we're here Monday through Sunday." },
  ];

  const steps = [
    { num: "01", title: "Tell Us Your Needs",   desc: "Give us a call or send us an email. No long questionnaires." },
    { num: "02", title: "We Shop the Market",   desc: "Our brokers compare rates across multiple trusted Canadian insurers for you." },
    { num: "03", title: "Get Your Best Option", desc: "We present your options clearly and help you choose the right fit." },
    { num: "04", title: "You're Covered",       desc: "We handle the paperwork and make sure your policy is active and accurate." },
  ];

  return (
    <>
    <PageSEO page="home" />

    <main className="home">

      {/* ── HERO ─────────────────────────────────────────── */}
      <section className="hero">
        <div className="hero-inner">
          <span className="hero-tag">Surrey's Trusted Insurance Brokers</span>
          <h1>Coverage You Can <em>Actually Count On</em></h1>
          <p>
            Independent, licensed brokers serving Surrey and Greater Vancouver for over 20 years.
            We compare dozens of insurers so you get the right coverage at the best price — every time.
          </p>
          <div className="hero-actions">
            <a href="#quote" className="btn-primary">Get a Free Quote</a>
            <a href="/services" className="btn-ghost">View Our Services</a>
          </div>
        </div>
      </section>

      {/* ── TRUST STRIP ──────────────────────────────────── */}
      <section className="trust-strip">
        <div className="trust-item"><strong>5,000+</strong><span>Clients Served</span></div>
        <div className="trust-divider" />
        <div className="trust-item"><strong>20+</strong><span>Years in Business</span></div>
        <div className="trust-divider" />
        <div className="trust-item"><strong>15+</strong><span>Insurance Products</span></div>
        <div className="trust-divider" />
        <div className="trust-item"><strong>100%</strong><span>Licensed Brokers</span></div>
      </section>

      {/* ── SERVICES ─────────────────────────────────────── */}
      <section className="services-section">
        <div className="section-header">
          <h2>Our Insurance Services</h2>
          <p>From auto and home to business and travel — we have a policy that fits your needs.</p>
        </div>
        <div className="services-grid">
          {services.map((s, i) => (
            <div
              className="service-card"
              key={i}
              onClick={() => goToService(s.id)}
              style={{ cursor: "pointer" }}
            >
              <div className="service-card-icon">{s.icon}</div>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
              <span className="service-link">Learn more <ArrowRight size={14} /></span>
            </div>
          ))}
        </div>
      </section>

      {/* ── WHY CHOOSE US ────────────────────────────────── */}
      <section className="why-section">
        <div className="section-header light">
          <h2>Why Choose Keystone?</h2>
          <p>We're not tied to any single insurer — our only job is finding the best deal for you.</p>
        </div>
        <div className="features-grid">
          {features.map((f, i) => (
            <div className="feature-card" key={i}>
              <div className="feature-icon">{f.icon}</div>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── HOW IT WORKS ─────────────────────────────────── */}
      <section className="process-section">
        <div className="section-header">
          <h2>How It Works</h2>
          <p>Getting insured through Keystone is simple — here's what to expect.</p>
        </div>
        <div className="steps-grid">
          {steps.map((s, i) => (
            <div className="step-card" key={i}>
              <span className="step-num">{s.num}</span>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── REVIEWS ──────────────────────────────────────── */}
      <section className="reviews-section">
        <div className="section-header light">
          <h2>What Our Clients Say</h2>
          <p>Real reviews from real clients — hear from the people we've helped.</p>
        </div>
        <div className="reviews-wrapper">
          <button className="nav-btn" onClick={prevSlide} aria-label="Previous">
            <ChevronLeft size={20} />
          </button>
          <div className="reviews-grid">
            {displayed.map((r, i) => (
              <div className="review-card" key={i}>
                <div className="stars">
                  {Array.from({ length: r.rating }).map((_, j) => (
                    <Star key={j} size={15} className="star" />
                  ))}
                </div>
                <p>"{r.text}"</p>
                <div className="reviewer">
                  <div className="reviewer-avatar">{r.name.charAt(0)}</div>
                  <span>{r.name}</span>
                </div>
              </div>
            ))}
          </div>
          <button className="nav-btn" onClick={nextSlide} aria-label="Next">
            <ChevronRight size={20} />
          </button>
        </div>
        <div className="review-dots">
          {reviews.map((_, i) => (
            <button
              key={i}
              className={`dot ${i === reviewIndex ? "active" : ""}`}
              onClick={() => setReviewIndex(i)}
              aria-label={`Go to review ${i + 1}`}
            />
          ))}
        </div>
      </section>

      {/* ── GET A QUOTE CTA ──────────────────────────────── */}
      <section id="quote" className="quote-section">
        <div className="quote-cta-box">
          <PhoneCall size={44} className="quote-cta-icon" />
          <h2>Ready to Get a Quote?</h2>
          <p>
            Our licensed brokers are standing by to help you find the right
            coverage at the best price. Give us a call or send us an email —
            no forms, no waiting, just honest advice with no obligation.
          </p>
          <div className="quote-cta-actions">
            <a href="tel:6045958666" className="btn-primary">
              <Phone size={16} /> Call (604) 595-8666
            </a>
            <a href="mailto:Keystoneins@shaw.ca" className="btn-ghost">
              <Mail size={16} /> Keystoneins@shaw.ca
            </a>
          </div>
          <ul className="quote-perks">
            <li><CheckCircle size={15} /> No obligation, ever</li>
            <li><CheckCircle size={15} /> We compare multiple insurers</li>
            <li><CheckCircle size={15} /> Plain-language advice</li>
            <li><CheckCircle size={15} /> Fast, friendly response</li>
          </ul>
        </div>
      </section>

      {/* ── CONTACT BAR ──────────────────────────────────── */}
      <section className="contact-bar">
        <div className="contact-bar-inner">
          <div className="contact-bar-item">
            <MapPin size={20} />
            <div>
              <strong>Visit Us</strong>
              <span>103-8077 King George Blvd, Surrey, BC V3W 5B4</span>
            </div>
          </div>
          <div className="contact-bar-item">
            <Phone size={20} />
            <div>
              <strong>Call Us</strong>
              <a href="tel:6045958666">(604) 595-8666</a>
            </div>
          </div>
          <div className="contact-bar-item">
            <Mail size={20} />
            <div>
              <strong>Email Us</strong>
              <a href="mailto:Keystoneins@shaw.ca">Keystoneins@shaw.ca</a>
            </div>
          </div>
          <div className="contact-bar-item">
            <Clock size={20} />
            <div>
              <strong>Office Hours</strong>
              <span>Mon–Fri: 9:00 am – 8:00 pm &nbsp;|&nbsp; Sat–Sun: 10:00 am – 7:00 pm</span>
            </div>
          </div>
        </div>
      </section>

    </main>
    </>
  );
}